import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MdArrowBack } from 'react-icons/md';
import axiosInstance from '../../api/axiosInstance';

const BlogEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    author: 'Admin',
  });
  const [image, setImage] = useState(null);       // new file selected by admin
  const [existingImage, setExistingImage] = useState(''); // image already saved in DB
  const [preview, setPreview] = useState('');     // preview shown in UI
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Fetch blog data
    const fetchBlog = async () => {
      try {
        const response = await axiosInstance.get(`/blog/${id}`);
        if (response.data.success) {
          setFormData({
            title: response.data.data.title,
            description: response.data.data.description || '',
            author: response.data.data.author || 'Admin',
          });
          setExistingImage(response.data.data.image || '');
          setPreview(response.data.data.image || '');
        }
      } catch (err) {
        console.error('Failed to fetch blog');
      }
    };
    fetchBlog();
  }, [id]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Image is required — must have either an existing image or a newly selected one
    if (!existingImage && !image) {
      alert('⚠️ Please select a Cover Image before updating the blog.');
      return;
    }

    setLoading(true);

    const formDataToSend = new FormData();
    formDataToSend.append('title', formData.title);
    formDataToSend.append('description', formData.description);
    formDataToSend.append('author', formData.author);
    if (image) {
      formDataToSend.append('image', image);
    }

    try {
      const response = await axiosInstance.put(`/blog/${id}`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        navigate('/admin/blogs');
      } else {
        alert(response.data.message || 'Failed to update blog');
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <button
        onClick={() => navigate('/admin/blogs')}
        className="flex items-center gap-2 text-gray-600 hover:text-[#008300] mb-4 sm:mb-6"
      >
        <MdArrowBack />
        <span>Back to Blogs</span>
      </button>

      <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-6 sm:mb-8">Edit Blog</h1>

      <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 border border-gray-100">
        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Title
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008300] focus:border-transparent outline-none text-sm sm:text-base"
              placeholder="Enter blog title"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description <span className="text-gray-400 font-normal text-xs sm:text-sm">(Hero section mein Overview ke niche dikhega)</span>
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="4"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008300] focus:border-transparent outline-none resize-none text-sm sm:text-base"
              placeholder="Blog ka short summary likho jo Overview section mein dikhega..."
            />
          </div>

          {/* Author */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Author
            </label>
            <input
              type="text"
              name="author"
              value={formData.author}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008300] focus:border-transparent outline-none text-sm sm:text-base"
            />
          </div>

          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Cover Image <span className="text-red-500">*</span>
              <span className="text-gray-400 font-normal ml-2 text-xs sm:text-sm">(To change the image, select a new one)</span>
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 sm:p-6 text-center hover:border-[#008300] transition-colors">
              {preview ? (
                <img
                  src={preview}
                  alt="Preview"
                  className="max-h-48 sm:max-h-64 mx-auto rounded-lg"
                />
              ) : (
                <div>
                  <p className="text-gray-500 mb-2 text-sm sm:text-base">
                    Drag and drop an image, or click to select
                  </p>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                    id="image-upload"
                  />
                  <label
                    htmlFor="image-upload"
                    className="cursor-pointer text-[#008300] font-medium text-sm sm:text-base"
                  >
                    Choose File
                  </label>
                </div>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#008300] text-white py-3 rounded-lg font-semibold hover:bg-[#006600] transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
          >
            {loading ? 'Updating...' : 'Update Blog'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default BlogEdit;
